// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ContentPageCollectionModel } from "@mcmc/App/Services/ContentPageSearchService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { ContentGroupKey, getGroupKey } from "@mcmc/App/Store/Reducers";

export const SearchTypes = {
    PRODUCTS: "product",
    EVENTS: "event",
    PAGES: "page",
    RESOURCES: "resource",
};

export function getContentPageDataview(state: McMcApplicationState, contentGroup?: ContentGroupKey) {
    const groupKey = getGroupKey(contentGroup);
    const { parameter } = state.xContentPagesSearch[groupKey];
    return parameter ? state.xContentPagesSearch[groupKey].dataViews[getDataViewKey(parameter)] : undefined;
}

export function getContentPageDataViewProperty<K extends keyof Omit<ContentPageCollectionModel, "ids">>(
    state: McMcApplicationState,
    key: K,
) {
    const contentPageDataView = getContentPageDataview(state);
    return contentPageDataView?.value ? contentPageDataView.value[key] : undefined;
}

export function getSearchType(state: ApplicationState) {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ searchType?: string }>(location.search);
    return parsedQuery.searchType?.toLowerCase() || SearchTypes.PRODUCTS;
}

export function getSearchTypeLabel(state: ApplicationState) {
    const searchType = getSearchType(state);
    switch (searchType) {
        case SearchTypes.PRODUCTS:
            return "Products";
        case SearchTypes.EVENTS:
            return "Training & Events";
        case SearchTypes.RESOURCES:
            return "Resources";
        case SearchTypes.PAGES:
            return "Pages";
    }
}

export function isProductSearchType(state: ApplicationState) {
    const searchType = getSearchType(state);
    return [SearchTypes.EVENTS, SearchTypes.RESOURCES, SearchTypes.PAGES].indexOf(searchType) === -1;
}

export function isEventSearchType(state: ApplicationState) {
    const searchType = getSearchType(state);
    return searchType === SearchTypes.EVENTS;
}

export function isContentSearchMode(state: ApplicationState) {
    const searchType = getSearchType(state);
    return [SearchTypes.RESOURCES, SearchTypes.PAGES].indexOf(searchType) > -1 && isSearchPage(state);
}

export function isSearchPage(state: ApplicationState) {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ query?: string }>(location.search);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const path =
        selectedCategoryPath || (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    return !!path.match(/^\/search\b/i) && !!parsedQuery.query;
}
